import { useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import { CSSObject, Theme, SxProps, TypographyVariant } from "@mui/material/styles";
import TextField, { type TextFieldProps } from "@mui/material/TextField";
import Tooltip, { TooltipProps as MuiTooltipProps } from "@mui/material/Tooltip";
import {
  ReactElement,
  ChangeEvent,
  FocusEvent,
  FocusEventHandler,
  KeyboardEvent,
  useEffect,
  useEffectEvent,
  useMemo,
  useState,
  useRef,
} from "react";
import * as React from "react";

export type InlineEditableTextFieldProps = {
  id?: string;
  ariaLabel?: string;
  autoSelect?: boolean;
  autoFocus?: boolean;
  fitWidthToContent?: boolean;
  className?: string;
  defaultValue?: string;
  fullWidth?: boolean;
  inputSx?: SxProps<Theme>;
  maxLength?: number;
  maxRows?: number;
  /** Edge case: If we want maxRows to be 1 only when not focused, we need to override multiline */
  multiline?: boolean;
  name?: string;
  onBlur?: FocusEventHandler<HTMLTextAreaElement>;
  onChange?: (value: string) => void;
  onFocus?: FocusEventHandler<HTMLTextAreaElement>;
  placeholder?: string;
  readOnly?: boolean;
  size?: TextFieldProps["size"];
  sx?: SxProps<Theme>;
  TooltipProps?: Omit<
    MuiTooltipProps,
    "title" | "disableHoverListener" | "disableFocusListener" | "disableTouchListener" | "children"
  >;
  /**
   * Custom tooltip text (optional).
   * If not supplied, the tooltip shows the full text if it is cut off due to `maxRows`.
   */
  tooltipText?: string;
  /**
   * Custom tooltip text depending on whether the text is overflowing (optional).
   * Only used if `tooltipText` is not supplied.
   * @param isOverflowing
   */
  conditionalTooltipText?: (isOverflowing: boolean) => string | undefined;
  typographyVariant?: TypographyVariant;
  useDefaultValueAsPlaceholder?: boolean;
  error?: boolean;
  "data-testid"?: string;
};

const getHorizontalPadding = (element: HTMLElement | null): number => {
  if (element == null) return 0;

  const { paddingLeft, paddingRight } = window.getComputedStyle(element);

  return (Number.parseFloat(paddingLeft) || 0) + (Number.parseFloat(paddingRight) || 0);
};

export const InlineEditableTextField = ({
  id,
  ariaLabel,
  autoFocus,
  autoSelect,
  className,
  defaultValue,
  fitWidthToContent,
  fullWidth,
  inputSx: inputSxProp,
  maxLength,
  maxRows,
  multiline,
  name,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  readOnly,
  size,
  sx,
  TooltipProps,
  tooltipText,
  conditionalTooltipText,
  typographyVariant,
  useDefaultValueAsPlaceholder,
  error,
  "data-testid": dataTestId,
}: InlineEditableTextFieldProps): ReactElement => {
  const [value, setValue] = useState(!useDefaultValueAsPlaceholder ? defaultValue : "");
  const [isFocused, setIsFocused] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const effectiveMultiline = multiline ?? maxRows !== 1;

  const theme = useTheme();

  const inputSx = useMemo((): SxProps<Theme> => {
    const variantTypography =
      typographyVariant == null
        ? undefined
        : (theme.typography[typographyVariant] as CSSObject | undefined);
    const inputTypographySx =
      variantTypography == null
        ? {}
        : {
            "& .MuiInputBase-input": {
              ...variantTypography,
            },
          };

    if (effectiveMultiline) {
      inputSxProp = {
        ...inputSxProp,
        height: "auto",
      };
    }

    return {
      ...inputSxProp,
      ...inputTypographySx,
      ...variantTypography,
      ...(!readOnly &&
        !isFocused &&
        !error && {
          borderColor: "transparent",
          backgroundColor: "transparent",
          outline: "none",
          boxShadow: "none",
          ".MuiOutlinedInput-notchedOutline": {
            borderColor: "transparent",
          },
          "&:hover": {
            borderColor: "transparent !important",
            backgroundColor: (theme) => theme.vars.palette.surface.overlay,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "transparent !important",
            },
          },
        }),
      ...((readOnly || !isFocused) &&
        effectiveMultiline && {
          textarea: {
            WebkitLineClamp: maxRows,
            overflow: "hidden",
            display: "-webkit-box",
            wordBreak: maxRows === 1 ? "break-all" : "normal",
            WebkitBoxOrient: "vertical",
          },
        }),
      ...((readOnly || !isFocused) &&
        !effectiveMultiline && {
          input: {
            overflow: "hidden",
            textOverflow: "ellipsis",
            wordBreak: "break-all",
          },
        }),
    } as SxProps<Theme>;
  }, [
    error,
    effectiveMultiline,
    inputSxProp,
    isFocused,
    maxRows,
    readOnly,
    theme,
    typographyVariant,
  ]);

  const inputRef = useRef<HTMLTextAreaElement>(undefined);

  const resizeObserver = React.useRef(
    new ResizeObserver((entries) => {
      if (inputRef.current) {
        setIsOverflowing(inputRef.current.scrollHeight > entries[0].contentRect.height);
      }
    }),
  );

  React.useLayoutEffect(() => {
    const observer = resizeObserver.current;
    const textarea = inputRef.current;
    if (textarea) {
      observer.observe(textarea);
    }

    return () => {
      if (textarea) {
        observer.unobserve(textarea);
      }
    };
  }, [resizeObserver]);

  const disableTooltip = useMemo(() => {
    // Focus suppresses the tooltip so it does not sit over the text being edited. A read-only
    // field takes focus without entering an edit, so it keeps its overflow tooltip — often the
    // only way to read a clipped value.
    if (isFocused && !readOnly) return true;
    if (tooltipText) return false;
    if (isOverflowing) return false;
    if (conditionalTooltipText?.(isOverflowing)) return false;
    return true;
  }, [conditionalTooltipText, isFocused, isOverflowing, readOnly, tooltipText]);

  const handleOpenTooltip = () => {
    if (!disableTooltip) {
      setTooltipOpen(true);
    }
  };

  const handleCloseTooltip = () => {
    setTooltipOpen(false);
  };

  const selectOnMount = useEffectEvent(() => {
    if (autoSelect) {
      inputRef.current?.select();
    }
  });

  useEffect(() => {
    selectOnMount();
  }, []);

  useEffect(() => {
    if (disableTooltip && tooltipOpen) {
      setTooltipOpen(false);
    }
  }, [disableTooltip, tooltipOpen]);

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) =>
    setValue(event.currentTarget.value.replace(/[\n\r\v]+/g, ""));

  const handleFocus = (event: FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(true);
    onFocus?.(event);
  };

  const handleBlur = (event: FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(false);
    const element = event.currentTarget;
    element.scrollTop = 0;
    if (!readOnly) {
      if (!element.value) {
        setValue(defaultValue);
        onChange?.(defaultValue || "");
      } else {
        onChange?.(element.value);
      }
    }
    onBlur?.(event);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // Prevent adding new lines via keyboard, instead stop editing
    if (event.code === "Enter") {
      event.currentTarget.blur();
      event.preventDefault();
    }
  };

  const [contentWidth, setContentWidth] = useState(0);
  const widthBoxRef = useRef<HTMLSpanElement>(null);

  React.useLayoutEffect(() => {
    const widthBox = widthBoxRef.current;
    const input = inputRef.current;
    if (!fitWidthToContent || widthBox == null || input == null || widthBox.offsetWidth === 0)
      return;

    const renderedPadding = getHorizontalPadding(input) + getHorizontalPadding(input.parentElement);
    // adding 1px prevents the browser from sometimes showing the ellipsis
    setContentWidth(widthBox.offsetWidth + renderedPadding + 1);
  }, [fitWidthToContent, inputSx, size, value]);

  return (
    <>
      {fitWidthToContent ? (
        <Box
          component="span"
          sx={{
            ...inputSx,
            visibility: "hidden",
            position: "absolute",
          }}
        >
          <Box
            component="span"
            ref={widthBoxRef}
            sx={{ display: "inline-block", whiteSpace: "pre" }}
          >
            {value}
          </Box>
        </Box>
      ) : null}
      <Tooltip
        {...TooltipProps}
        title={tooltipText || conditionalTooltipText?.(isOverflowing) || value || ""}
        disableFocusListener={disableTooltip}
        disableHoverListener={disableTooltip}
        disableTouchListener={disableTooltip}
        open={tooltipOpen}
        onOpen={handleOpenTooltip}
        onClose={handleCloseTooltip}
      >
        <TextField
          id={id}
          data-testid={dataTestId}
          error={error}
          // eslint-disable-next-line jsx-a11y/no-autofocus -- inline editors focus when activated so the user can name new items without extra clicks
          autoFocus={autoFocus}
          className={className}
          fullWidth={fullWidth}
          slotProps={{
            input: {
              sx: inputSx,
              readOnly,
              onKeyDown,
            },
            htmlInput: {
              maxLength,
              "aria-label": ariaLabel,
            },
          }}
          maxRows={!isFocused || readOnly ? maxRows : undefined}
          multiline={effectiveMultiline}
          name={name}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder={useDefaultValueAsPlaceholder ? defaultValue : placeholder}
          inputRef={inputRef}
          size={size}
          sx={{
            ...sx,
            ...(fitWidthToContent ? { width: contentWidth } : {}),
          }}
          value={value}
        />
      </Tooltip>
    </>
  );
};
